import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import SectionEditor from "@/components/SectionEditor";
import ListSectionEditor from "@/components/ListSectionEditor";
import ImageUpload from "@/components/ImageUpload";
import { DEFAULTS } from "@/lib/content_defaults";
import { Plus, Trash2, Save } from "lucide-react";

// ============================================================================
// HOME TAB
// ============================================================================
export function HomeCMS() {
  return (
    <div className="space-y-8">
      <SectionEditor title="Home — Hero" sectionKey="home_hero" defaults={DEFAULTS.home_hero} fields={[
        { key: "label_tag", label: "Top Label / Eyebrow", type: "text" },
        { key: "line1", label: "Title Line 1 (white)", type: "text" },
        { key: "line2", label: "Title Line 2 (gold)", type: "text" },
        { key: "line3", label: "Title Line 3 (silver)", type: "text" },
        { key: "subtitle", label: "Subtitle Paragraph", type: "textarea", rows: 3, full: true },
        { key: "cta_primary_text", label: "Primary Button Text", type: "text" },
        { key: "cta_primary_link", label: "Primary Button Link", type: "text" },
        { key: "cta_secondary_text", label: "Secondary Button Text", type: "text" },
        { key: "cta_secondary_link", label: "Secondary Button Link", type: "text" },
        { key: "cta_tertiary_text", label: "Third Button Text", type: "text" },
        { key: "cta_tertiary_link", label: "Third Button Link", type: "text" },
        { key: "hero_image", label: "Hero Background Image", type: "image", full: true },
      ]}/>
      <SectionEditor title="Home — Mission" sectionKey="home_mission" defaults={DEFAULTS.home_mission} fields={[
        { key: "label_tag", label: "Eyebrow Label", type: "text" },
        { key: "title", label: "Mission Title", type: "textarea", rows: 2 },
        { key: "paragraph_1", label: "Paragraph 1", type: "textarea", rows: 4, full: true },
        { key: "paragraph_2", label: "Paragraph 2", type: "textarea", rows: 4, full: true },
        { key: "cta1_text", label: "Button 1 Text", type: "text" },
        { key: "cta1_link", label: "Button 1 Link", type: "text" },
        { key: "cta2_text", label: "Button 2 Text", type: "text" },
        { key: "cta2_link", label: "Button 2 Link", type: "text" },
      ]}/>
      <ListSectionEditor title="Home — Stat Blocks" sectionKey="home_stats" defaults={DEFAULTS.home_stats}
        itemFields={[{ key: "number", label: "Number (e.g. 500+)", type: "text" }, { key: "label", label: "Label", type: "text" }]}
        addLabel="Add Stat"/>
      <SectionEditor title="Home — Features Header" sectionKey="home_features" defaults={DEFAULTS.home_features} fields={[
        { key: "label_tag", label: "Eyebrow", type: "text" },
        { key: "title", label: "Section Title", type: "text", full: true },
      ]}/>
      <ListSectionEditor title="Home — Feature Cards" sectionKey="home_features" defaults={DEFAULTS.home_features}
        itemFields={[
          { key: "title", label: "Card Title", type: "text" },
          { key: "text", label: "Card Text", type: "textarea" },
          { key: "icon", label: "Icon (Flame, Trophy, Target, Users, Zap, ShieldCheck, Award)", type: "text" },
        ]} addLabel="Add Feature Card"/>
      <SectionEditor title="Home — Bottom CTA + Newsletter" sectionKey="home_cta" defaults={DEFAULTS.home_cta} fields={[
        { key: "label_tag", label: "Eyebrow", type: "text" },
        { key: "title", label: "Big Title", type: "textarea", rows: 2 },
        { key: "subtitle", label: "Subtitle", type: "textarea", rows: 2 },
        { key: "cta_primary_text", label: "Primary Button Text", type: "text" },
        { key: "cta_primary_link", label: "Primary Button Link", type: "text" },
        { key: "cta_secondary_text", label: "Secondary Button Text", type: "text" },
        { key: "cta_secondary_link", label: "Secondary Button Link", type: "text" },
        { key: "newsletter_label", label: "Newsletter Eyebrow", type: "text" },
        { key: "newsletter_title", label: "Newsletter Title", type: "textarea", rows: 2, full: true },
      ]}/>
    </div>
  );
}

// ============================================================================
// PAGE HEROES TAB
// ============================================================================
export function PageHeroesCMS() {
  const shared = [
    { key: "label_tag", label: "Eyebrow", type: "text" },
    { key: "line1", label: "Title Line 1", type: "text" },
    { key: "line2", label: "Title Line 2 (gold)", type: "text" },
    { key: "line3", label: "Title Line 3", type: "text" },
    { key: "subtitle", label: "Subtitle", type: "textarea", rows: 3, full: true },
  ];
  return (
    <div className="space-y-8">
      <SectionEditor title="Programs Page Hero" sectionKey="page_hero_programs" defaults={DEFAULTS.page_hero_programs} fields={shared}/>
      <SectionEditor title="Coaches Page Hero" sectionKey="page_hero_coaches" defaults={DEFAULTS.page_hero_coaches} fields={shared}/>
      <SectionEditor title="Recruiting Page Hero" sectionKey="page_hero_recruiting" defaults={DEFAULTS.page_hero_recruiting} fields={[
        ...shared,
        { key: "cta_primary_text", label: "Primary CTA Text", type: "text" },
        { key: "cta_primary_link", label: "Primary CTA Link", type: "text" },
        { key: "cta_secondary_text", label: "Secondary CTA Text", type: "text" },
        { key: "cta_secondary_link", label: "Secondary CTA Link", type: "text" },
        { key: "hero_image", label: "Right-side Image", type: "image", full: true },
      ]}/>
      <SectionEditor title="Pricing Page Hero" sectionKey="page_hero_pricing" defaults={DEFAULTS.page_hero_pricing} fields={shared}/>
      <SectionEditor title="Events Page Hero" sectionKey="page_hero_events" defaults={DEFAULTS.page_hero_events} fields={shared}/>
      <SectionEditor title="Contact Page Hero" sectionKey="page_hero_contact" defaults={DEFAULTS.page_hero_contact} fields={shared}/>
    </div>
  );
}

// ============================================================================
// RECRUITING PILLARS TAB
// ============================================================================
export function RecruitingCMS() {
  return (
    <div className="space-y-6">
      <SectionEditor title="Recruiting — Pillars Section Header" sectionKey="recruiting_pillars" defaults={DEFAULTS.recruiting_pillars} fields={[
        { key: "section_label", label: "Eyebrow", type: "text" },
        { key: "section_title", label: "Section Title", type: "text", full: true },
      ]}/>
      <ListSectionEditor title="Recruiting — Pillars (up to 6 shown)" sectionKey="recruiting_pillars" defaults={DEFAULTS.recruiting_pillars}
        itemFields={[
          { key: "title", label: "Pillar Title", type: "text" },
          { key: "text", label: "Pillar Description", type: "textarea" },
        ]} addLabel="Add Pillar"/>
    </div>
  );
}

// ============================================================================
// CONTACT & SOCIAL TAB
// ============================================================================
export function ContactCMS() {
  return (
    <SectionEditor title="Contact Info + Social Links" sectionKey="contact_info" defaults={DEFAULTS.contact_info} fields={[
      { key: "email", label: "Contact Email", type: "text" },
      { key: "phone_1", label: "Phone #1 (e.g. 303.506.7541)", type: "text" },
      { key: "phone_2", label: "Phone #2 (optional)", type: "text" },
      { key: "address", label: "Physical Address (blank to hide)", type: "textarea", rows: 2, full: true },
      { key: "social_x", label: "Twitter/X URL", type: "text" },
      { key: "social_x_handle", label: "Twitter/X Handle Display (e.g. @8020VoodooBoyz)", type: "text" },
      { key: "social_instagram", label: "Instagram URL", type: "text" },
      { key: "social_instagram_handle", label: "Instagram Handle Display", type: "text" },
      { key: "social_facebook", label: "Facebook URL", type: "text" },
      { key: "social_youtube", label: "YouTube URL", type: "text" },
      { key: "social_tiktok", label: "TikTok URL", type: "text" },
      { key: "social_hudl", label: "Hudl URL (optional)", type: "text" },
    ]}/>
  );
}

// ============================================================================
// BRAND TAB
// ============================================================================
export function BrandCMS() {
  return (
    <SectionEditor title="Brand / Site Identity" sectionKey="brand" defaults={DEFAULTS.brand} fields={[
      { key: "site_name", label: "Site Name (top nav)", type: "text" },
      { key: "site_subname", label: "Sub-brand Line", type: "text" },
      { key: "monogram", label: "Monogram / Fallback Badge (2 chars)", type: "text" },
      { key: "tagline", label: "Footer Tagline", type: "text" },
      { key: "footer_about", label: "Footer About Paragraph", type: "textarea", rows: 3, full: true },
      { key: "logo_url", label: "Site Logo (replaces monogram badge)", type: "image", full: true },
    ]}/>
  );
}

// ============================================================================
// PROGRAMS CMS TAB
// ============================================================================
export function ProgramsCMS() {
  const [rows, setRows] = useState([]);
  const [newRow, setNewRow] = useState({ key:"", title:"", tagline:"", description:"", image:"", order:100 });

  const load = async () => {
    const { data } = await api.get("/programs");
    setRows(data.map(d => ({ ...d })));
  };
  useEffect(() => { load(); }, []);

  const update = (i, k, v) => { const c = [...rows]; c[i] = { ...c[i], [k]: v }; setRows(c); };
  const save = async (r) => {
    try { await api.post("/programs", { ...r, order: parseInt(r.order) || 100 }); toast.success("Saved."); load(); }
    catch { toast.error("Save failed."); }
  };
  const del = async (key) => {
    if (!confirm(`Delete program "${key}"?`)) return;
    await api.delete(`/programs/${key}`); toast.success("Deleted."); load();
  };
  const create = async (e) => {
    e.preventDefault();
    if (!newRow.key.match(/^[a-z0-9-]+$/)) return toast.error("Key must be lowercase letters/digits/dashes.");
    try {
      await api.post("/programs", { ...newRow, order: parseInt(newRow.order) || 100 });
      toast.success("Program created."); load();
      setNewRow({ key:"", title:"", tagline:"", description:"", image:"", order:100 });
    } catch { toast.error("Create failed."); }
  };

  return (
    <div className="space-y-4">
      {rows.map((r, i) => (
        <div key={r.key} className="card-light p-6" data-testid={`admin-program-${r.key}`}>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Key (url-safe)" v={r.key} disabled/>
            <Field label="Title" v={r.title} onC={(v)=>update(i,"title",v)} testid={`admin-program-title-${r.key}`}/>
            <Field label="Order" v={r.order} onC={(v)=>update(i,"order",v)} testid={`admin-program-order-${r.key}`}/>
            <Field label="Tagline" v={r.tagline} onC={(v)=>update(i,"tagline",v)} testid={`admin-program-tagline-${r.key}`}/>
            <div className="md:col-span-2">
              <label className="label-tag" style={{ color: "#000" }}>Description</label>
              <textarea value={r.description || ""} onChange={(e)=>update(i,"description",e.target.value)} rows={2}
                className="mt-2 w-full border border-black/15 bg-white p-3 text-sm outline-none focus:border-[color:var(--gold)]" data-testid={`admin-program-desc-${r.key}`}/>
            </div>
            <div className="md:col-span-3">
              <ImageUpload label="Program Image" value={r.image} onChange={(v)=>update(i,"image",v)} testid={`admin-program-image-${r.key}`}/>
              <input value={r.image || ""} onChange={(e)=>update(i,"image",e.target.value)} placeholder="or paste image URL" className="input-sharp mt-2 text-xs" data-testid={`admin-program-imageurl-${r.key}`}/>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={()=>save(r)} className="btn-gold !py-2 !px-4 text-sm" data-testid={`admin-program-save-${r.key}`}><Save size={14}/>Save</button>
            <button onClick={()=>del(r.key)} className="text-red-500" data-testid={`admin-program-delete-${r.key}`}><Trash2 size={16}/></button>
          </div>
        </div>
      ))}

      <form onSubmit={create} className="card-light p-6 border-dashed border-2 border-black/20">
        <div className="font-heading uppercase font-bold mb-4">Add New Program</div>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Key (e.g. qb-training)" v={newRow.key} onC={(v)=>setNewRow({...newRow,key:v.toLowerCase()})} testid="admin-program-new-key"/>
          <Field label="Title" v={newRow.title} onC={(v)=>setNewRow({...newRow,title:v})} testid="admin-program-new-title"/>
          <Field label="Order" v={newRow.order} onC={(v)=>setNewRow({...newRow,order:v})} testid="admin-program-new-order"/>
          <Field label="Tagline" v={newRow.tagline} onC={(v)=>setNewRow({...newRow,tagline:v})} testid="admin-program-new-tagline"/>
          <div className="md:col-span-2">
            <label className="label-tag" style={{ color: "#000" }}>Description</label>
            <textarea value={newRow.description} onChange={(e)=>setNewRow({...newRow,description:e.target.value})} rows={2}
              className="mt-2 w-full border border-black/15 bg-white p-3 text-sm outline-none focus:border-[color:var(--gold)]" data-testid="admin-program-new-desc"/>
          </div>
        </div>
        <div className="mt-3">
          <ImageUpload label="Program Image" value={newRow.image} onChange={(v)=>setNewRow({...newRow,image:v})} testid="admin-program-new-image"/>
        </div>
        <button className="btn-gold mt-4" data-testid="admin-program-new-create"><Plus size={14}/>Add Program</button>
      </form>
    </div>
  );
}

// ============================================================================
// FAQ CMS TAB
// ============================================================================
export function FAQCMS() {
  const [rows, setRows] = useState([]);
  const [newRow, setNewRow] = useState({ q: "", a: "", order: 100 });

  const load = async () => { const { data } = await api.get("/faq"); setRows(data); };
  useEffect(() => { load(); }, []);

  const isDB = rows.length > 0 && rows[0].id !== undefined;

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/faq", { ...newRow, order: parseInt(newRow.order) || 100 });
      toast.success("FAQ added."); setNewRow({ q:"", a:"", order:100 }); load();
    } catch { toast.error("Failed."); }
  };
  const save = async (r) => {
    try { await api.patch(`/faq/${r.id}`, { q: r.q, a: r.a, order: parseInt(r.order)||100 }); toast.success("Saved."); load(); }
    catch { toast.error("Save failed."); }
  };
  const del = async (id) => { if(!confirm("Delete?"))return; await api.delete(`/faq/${id}`); load(); };
  const update = (i, k, v) => { const c = [...rows]; c[i] = { ...c[i], [k]: v }; setRows(c); };

  return (
    <div className="space-y-4">
      {!isDB && rows.length > 0 && (
        <div className="card-light p-4 text-xs italic border-l-4 border-[color:var(--gold)]">
          Showing <strong>default</strong> FAQ. Add your first custom FAQ below — defaults will be hidden once you do.
        </div>
      )}
      {rows.map((r, i) => (
        <div key={r.id || i} className="card-light p-6">
          <div className="grid md:grid-cols-6 gap-3">
            <div className="md:col-span-5">
              <label className="label-tag" style={{ color: "#000" }}>Question</label>
              <input value={r.q || ""} onChange={(e)=>update(i,"q",e.target.value)} className="input-sharp mt-1" disabled={!r.id}/>
            </div>
            <Field label="Order" v={r.order} onC={(v)=>update(i,"order",v)} disabled={!r.id}/>
            <div className="md:col-span-6">
              <label className="label-tag" style={{ color: "#000" }}>Answer</label>
              <textarea value={r.a || ""} onChange={(e)=>update(i,"a",e.target.value)} rows={2}
                className="mt-1 w-full border border-black/15 bg-white p-2 text-sm outline-none focus:border-[color:var(--gold)]" disabled={!r.id}/>
            </div>
          </div>
          {r.id && (
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={()=>save(r)} className="btn-gold !py-2 !px-4 text-sm"><Save size={14}/>Save</button>
              <button onClick={()=>del(r.id)} className="text-red-500"><Trash2 size={16}/></button>
            </div>
          )}
        </div>
      ))}
      <form onSubmit={create} className="card-light p-6 border-dashed border-2 border-black/20">
        <div className="font-heading uppercase font-bold mb-3">Add New FAQ</div>
        <input required placeholder="Question" value={newRow.q} onChange={(e)=>setNewRow({...newRow,q:e.target.value})} className="input-sharp" data-testid="admin-faq-new-q"/>
        <textarea required placeholder="Answer" value={newRow.a} onChange={(e)=>setNewRow({...newRow,a:e.target.value})} rows={3}
          className="mt-3 w-full border border-black/15 bg-white p-3 text-sm outline-none focus:border-[color:var(--gold)]" data-testid="admin-faq-new-a"/>
        <input placeholder="Order" value={newRow.order} onChange={(e)=>setNewRow({...newRow,order:e.target.value})} className="input-sharp mt-3" data-testid="admin-faq-new-order"/>
        <button className="btn-gold mt-4" data-testid="admin-faq-new-create"><Plus size={14}/>Add FAQ</button>
      </form>
    </div>
  );
}

function Field({ label, v, onC, disabled, testid }) {
  return (
    <div>
      <label className="label-tag" style={{ color:"#000" }}>{label}</label>
      <input disabled={disabled} value={v ?? ""} onChange={onC?(e)=>onC(e.target.value):undefined} className={`input-sharp mt-1 ${disabled?"opacity-60":""}`} data-testid={testid}/>
    </div>
  );
}
